import React, { useEffect, useRef, useState } from "react";
import apiRoutes from "../../api/apiRoutes";
import { apiFetchJson } from "../../utils/http";

const PAGE_SIZE = 100;
const DEBOUNCE_MS = 300;

export default function ContratoAutocomplete({
  value,
  onChange,
  disabled = false,
  placeholder = "Buscar contrato...",
}) {
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  // Estados
  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState("");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // Sincronizar value → input
  useEffect(() => {
    if (value?.codigo) {
      setInputText(value.codigo);
    }
    if (!value) {
      setInputText("");
    }
  }, [value]);

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handleOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // Fetch
  const fetchContratos = async ({ pageIndex = 0, append = false, query }) => {
    if (loading) return;
    setLoading(true);

    const url = apiRoutes.contratos.autocomplete(query, pageIndex, PAGE_SIZE);

    try {
      const response = await apiFetchJson(url, true);
      const responseItems = response?.items ?? [];

      setItems((prev) =>
        append ? [...prev, ...responseItems] : responseItems
      );
      setHasMore(!!response?.hasMore);
      setPage(pageIndex);
    } catch {
      if (!append) {
        setItems([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Input con debounce
  const handleInputChange = (e) => {
    const text = e.target.value;
    setInputText(text);
    setOpen(true);

    if (value && text.trim() === "") {
      onChange(null);
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchContratos({
        pageIndex: 0,
        append: false,
        query: text.trim(),
      });
    }, DEBOUNCE_MS);
  };

  const handleFocus = () => {
    setOpen(true);
    fetchContratos({
      pageIndex: 0,
      append: false,
      query: inputText.trim(),
    });
  };

  // Scroll infinito
  const handleScroll = (e) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;

    if (nearBottom && hasMore && !loading) {
      fetchContratos({
        pageIndex: page + 1,
        append: true,
        query: inputText.trim(),
      });
    }
  };

  // Seleccionar
  const handleSelect = (contrato) => {
    onChange(contrato);
    setInputText(contrato.codigo);
    setOpen(false);
  };

  // Limpiar
  const clearSelection = () => {
    onChange(null);
    setInputText("");
    setItems([]);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative">
      <input
        type="text"
        className="input"
        disabled={disabled}
        placeholder={placeholder}
        value={inputText}
        onChange={handleInputChange}
        onFocus={handleFocus}
      />

      {value && (
        <button
          type="button"
          onClick={clearSelection}
          className="absolute right-3 top-2 text-gray-400"
          title="Limpiar"
        >
          ×
        </button>
      )}

      {open && (
        <div
          className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-64 overflow-auto text-sm"
          onScroll={handleScroll}
        >
          {loading && items.length === 0 && (
            <div className="px-3 py-2 text-gray-500">
              Cargando contratos...
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="px-3 py-2 text-gray-500">
              Sin resultados.
            </div>
          )}

          {items.map((c) => (
            <button
              key={c.id}
              type="button"
              className="block w-full text-left px-3 py-2 hover:bg-emerald-50"
              onClick={() => handleSelect(c)}
            >
              <div className="font-mono text-xs text-gray-700">
                {c.codigo}
              </div>
              {c.descricaoTexto && (
                <div className="text-sm text-gray-500 truncate">
                  {c.descricaoTexto}
                </div>
              )}
            </button>
          ))}

          {loading && items.length > 0 && (
            <div className="px-3 py-2 text-gray-400 text-xs">
              Cargando más…
            </div>
          )}
        </div>
      )}
    </div>
  );
}